import { supabase } from "@/integrations/supabase/client";
import { ClientService } from "@/services/ClientService";
import { CreditManagementService } from "@/services/CreditManagementService";

/**
 * Debug utilities for credit management system
 */
export class CreditDebugUtils {
  /**
   * Debug a client's credit calculation
   */
  static async debugClientCredit(clientId: string) {
    console.log("=== CREDIT DEBUG FOR CLIENT:", clientId, "===");

    try {
      // 1. Get client info
      const client = await ClientService.getClientById(clientId);
      console.log("Client info:", {
        id: client?.id,
        name: client?.name,
        currentCreditBalance: client?.currentCreditBalance,
        creditLimit: client?.creditLimit,
      });

      // 2. Get all invoices for this client
      const { data: invoices, error: invoicesError } = await supabase
        .from("invoices")
        .select("id, invoice_number, total_amount, payment_status, created_at")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false });

      if (invoicesError) {
        console.error("Error fetching invoices:", invoicesError);
        return;
      }

      console.log("All invoices for client:", invoices);

      // 3. Get unpaid invoices specifically
      const { data: unpaidInvoices, error: unpaidError } = await supabase
        .from("invoices")
        .select("id, invoice_number, total_amount, payment_status")
        .eq("client_id", clientId)
        .in("payment_status", ["unpaid", "partial", "overdue"]);

      if (unpaidError) {
        console.error("Error fetching unpaid invoices:", unpaidError);
        return;
      }

      console.log("Unpaid invoices:", unpaidInvoices);

      // 4. Calculate expected credit balance
      let expectedBalance = 0;
      for (const invoice of unpaidInvoices || []) {
        if (
          invoice.payment_status === "unpaid" ||
          invoice.payment_status === "overdue"
        ) {
          expectedBalance += invoice.total_amount;
        } else if (invoice.payment_status === "partial") {
          const paidAmount = await ClientService.getInvoicePaidAmount(
            invoice.id
          );
          const remainingAmount = invoice.total_amount - paidAmount;
          expectedBalance += Math.max(0, remainingAmount);
          console.log(
            `Partial invoice ${invoice.invoice_number}: total=${invoice.total_amount}, paid=${paidAmount}, remaining=${remainingAmount}`
          );
        }
      }

      console.log("Expected credit balance:", expectedBalance);
      console.log("Actual credit balance:", client?.currentCreditBalance);

      // 5. Recalculate and see if it updates
      console.log("Recalculating credit balance...");
      const newBalance = await CreditManagementService.recalculateClientCredit(
        clientId
      );
      console.log("New balance after recalculation:", newBalance);

      // 6. Get updated client info
      const updatedClient = await ClientService.getClientById(clientId);
      console.log(
        "Updated client credit balance:",
        updatedClient?.currentCreditBalance
      );

      return {
        client,
        invoices,
        unpaidInvoices,
        expectedBalance,
        newBalance,
        updatedClient,
      };
    } catch (error) {
      console.error("Error in credit debug:", error);
      throw error;
    }
  }

  /**
   * Test creating an invoice and see if credit updates
   */
  static async testInvoiceCreation(clientId: string, amount: number) {
    console.log("=== TESTING INVOICE CREATION ===");
    console.log("Client ID:", clientId);
    console.log("Invoice amount:", amount);

    // Get initial credit balance
    const initialClient = await ClientService.getClientById(clientId);
    console.log("Initial credit balance:", initialClient?.currentCreditBalance);

    // Create a test invoice directly in the database
    const { data: invoice, error } = await supabase
      .from("invoices")
      .insert({
        invoice_number: `TEST-${Date.now()}`,
        client_id: clientId,
        client_name: initialClient?.name || "Test Client",
        subtotal: amount,
        tax_rate: 0,
        tax_amount: 0,
        discount_amount: 0,
        total_amount: amount,
        payment_status: "unpaid",
        repair_shop_id: initialClient?.repairShopId,
        created_by: "00000000-0000-0000-0000-000000000000", // Dummy user ID
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating test invoice:", error);
      return;
    }

    console.log("Created test invoice:", invoice);

    // Wait a moment for real-time updates
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Check if credit balance updated
    const updatedClient = await ClientService.getClientById(clientId);
    console.log(
      "Credit balance after invoice creation:",
      updatedClient?.currentCreditBalance
    );

    // Manual recalculation
    const recalculatedBalance =
      await CreditManagementService.recalculateClientCredit(clientId);
    console.log("Manual recalculation result:", recalculatedBalance);

    // Final client state
    const finalClient = await ClientService.getClientById(clientId);
    console.log("Final credit balance:", finalClient?.currentCreditBalance);

    return {
      initialBalance: initialClient?.currentCreditBalance,
      invoice,
      updatedBalance: updatedClient?.currentCreditBalance,
      recalculatedBalance,
      finalBalance: finalClient?.currentCreditBalance,
    };
  }

  /**
   * List all clients with their credit info
   */
  static async listAllClientsCredit(repairShopId: string) {
    console.log("=== ALL CLIENTS CREDIT INFO ===");

    const clients = await ClientService.getClients(repairShopId);

    for (const client of clients) {
      console.log(`Client: ${client.name} (${client.id})`);
      console.log(`  Credit Balance: ${client.currentCreditBalance}`);
      console.log(`  Credit Limit: ${client.creditLimit}`);

      const { data: invoices } = await supabase
        .from("invoices")
        .select("id, total_amount, payment_status")
        .eq("client_id", client.id);

      const unpaidTotal =
        invoices?.reduce((sum, inv) => {
          if (
            inv.payment_status === "unpaid" ||
            inv.payment_status === "overdue"
          ) {
            return sum + inv.total_amount;
          }
          return sum;
        }, 0) || 0;

      console.log(`  Unpaid invoices total: ${unpaidTotal}`);
      console.log(
        `  Match: ${client.currentCreditBalance === unpaidTotal ? "YES" : "NO"}`
      );
      console.log("---");
    }
  }
}

// Make it available globally for debugging in console
(window as any).creditDebug = CreditDebugUtils;
